import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyEmail } from '../../../app/store/slices/authSlice';
import { resendVerification } from '../../../app/store/slices/authSlice';
import logo from '../../../assets/images/logos/576_Cash Canvas-02.png';
import { toast } from 'react-toastify';

const EmailVerification = () => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useSelector((state) => state.auth);
  const email = location.state?.email;
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');


  // Handle resend OTP
  const handleResendVerificationOTP = async () => {
    try {
      setResendLoading(true);
      setResendMessage('');
      const result = await dispatch(resendVerification({ email })).unwrap();

      if (result.error) {
        switch (result.error.code) {
          case 'USER_NOT_FOUND':
            toast.error('No account found with this email');
            navigate('/signup');
            break;
          default:
            toast.error(result.message || 'Failed to resend verification code');
        }
        return;
      }

      // Determine cooldown time
      const cooldown = resendAttempts === 0 ? 60 : 120;
      setResendCooldown(cooldown);

      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) clearInterval(interval);
          return prev > 0 ? prev - 1 : 0;
        });
      }, 1000);

      setResendAttempts((prev) => prev + 1);
      toast.success('Verification code sent successfully!');
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(err.message || 'Failed to resend verification code');
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !otp) {
      toast.error('Email and verification code are required');
      return;
    }

    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    try {
      const result = await dispatch(verifyEmail({ email, otp })).unwrap();
      
      if (result.error) {
        switch (result.error.code) {
          case 'USER_NOT_FOUND':
            toast.error('No account found with this email');
            navigate('/signup');
            break;
          case 'OTP_EXPIRED':
            toast.error('Verification code has expired. Please request a new one.');
            setOtp('');
            break;
          case 'INVALID_OTP':
            toast.error('Incorrect verification code');
            setOtp('');
            break;
          case 'VERIFICATION_ERROR':
            toast.error('Verification failed. Please try again.');
            break;
          default:
            toast.error(result.message || 'Verification failed');
        }
        return;
      }

      if (result.status === 200) {
        toast.success('Email verified successfully!');
        navigate('/login', {
          state: { message: 'Email verified successfully! Please login to continue.' }
        });
      }
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(err.message || 'Verification failed. Please try again.');
      }
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setOtp(value);
      setError('');
    }
  };

  return (
    <div className="flex-1 min-h-screen flex items-center justify-center bg-gray-50 px-4 font-sans">
      {/* Logo container with smaller responsive sizing */}
    <div className="fixed top-0 left-0 p-4">
      <img 
        src={logo} 
        alt="Cash Canvas Logo" 
        className="h-24 w-auto object-contain max-w-[150px]"
      />
    </div>
      
      <div className="auth-ui-card">
        <h2 className="txt-page-heading">
          Verify Your Email
        </h2>

        <p className="text-sm text-gray-600 mt-4 mb-6 text-center">
          We've sent a 6-digit verification code to<br />
          <span className="font-medium text-gray-900">{email}</span>
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {resendMessage && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
            {resendMessage}
          </div>
        )}


        <form onSubmit={handleVerify} className="space-y-6">
          <div className="flex justify-center gap-2">
            <input
              type="text"
              value={otp}
              onChange={handleOtpChange}
              placeholder="------"
              className="w-full px-4 py-3 border text-center text-2xl tracking-[0.2em] border-gray-400 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-0 focus:border-gray-400"
              maxLength="6"
              pattern="\d{6}"
              required
            />
          </div>

          <button
            type="submit"
            className={`
             submit-btn disabled:cursor-not-allowed
            `}
            disabled={status === 'loading' || otp.length !== 6}
          >
            {status === 'loading' ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-4">
          <p className="text-sm text-gray-600">
            Didn't receive the code?{' '}
            <button
              className="text-btn-primary hover:text-btn-primary-hover font-medium"
              onClick={handleResendVerificationOTP}
              disabled={resendCooldown > 0 || status === 'loading'}
            >
             {resendLoading ? 'Resending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </button>
          </p>

          <div className="border-t border-gray-200 pt-6">
            <Link
              to="/login"
              className="text-sm text-btn-primary hover:text-btn-primary-hover font-medium"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;